import { Job, Task } from "@freeconvert/freeconvert-node/dist/types";
import { CompressService } from "client/services/Compress/CompressService";
import { deleteImage, setCompressed, setError } from "store/features/compress/actions";
import { useAppDispatch } from "store/store";
import { CompressImage, CompressState } from "store/features/compress/types";
import { PayloadAction } from "@reduxjs/toolkit";
import { useRef } from "react";
import { MAX_RETRY_COUNT } from "client/config/configs";
import { createCompressJob } from "store/features/compress/extraReducers/createCompressJob";
import { useDailyQuota } from "@/modules/DailyQuota/hooks/useDailyQuota";

type CreateCompressJob = (
    image: CompressImage,
    importTask: Task,
    settings: CompressState["compressionSettings"],
    filename: string,
) => Promise<void>;

export const useCompress = () => {
    const dispatch = useAppDispatch();
    const retryCountRef = useRef(0);
    const { consumeQuota } = useDailyQuota();

    const handleCreateCompressJob: CreateCompressJob = async (image, importTask, settings, filename) => {
        const result = await dispatch(
            createCompressJob({
                image,
                importTask,
                settings,
                filename,
            }),
        );
        /**
         * We only reduce 1 quota per image, regardless of compression count
         */
        if (!image.compressed) {
            consumeQuota(1);
        }
        const payload = result.payload as PayloadAction<{ result: Job }>["payload"];

        if (result.meta.requestStatus === "rejected") {
            const errorCode = result.payload?.error?.code;
            const shouldRetry = !(errorCode && !isNaN(+errorCode) && +errorCode < 500);
            if (shouldRetry) {
                setTimeout(() => {
                    retryCreateCompressJob(image, importTask, settings, filename);
                }, 5000);
            } else {
                dispatch(
                    setError({
                        imageId: image.id,
                        error: {
                            message: "compression failed",
                            state: "compressing",
                        },
                    }),
                );
            }
        } else if (result.meta.requestStatus === "fulfilled" && result.payload) {
            handleCreateCompressJobSuccess(image, payload.result, settings);
        }
    };

    const handleCreateCompressJobFailed = (image: CompressImage) => {
        dispatch(deleteImage(image.id));
    };

    const retryCreateCompressJob: CreateCompressJob = async (image, importTask, settings, filename) => {
        if (retryCountRef.current >= MAX_RETRY_COUNT) {
            handleCreateCompressJobFailed(image);
            return;
        }
        retryCountRef.current++;
        const result = await dispatch(
            createCompressJob({
                image,
                importTask,
                settings,
                filename,
            }),
        );
        const payload = result.payload as PayloadAction<{ result: Job }>["payload"];
        if (result.meta.requestStatus === "fulfilled" && result.payload) {
            handleCreateCompressJobSuccess(image, payload.result, settings);
        } else {
            setTimeout(() => retryCreateCompressJob(image, importTask, settings, filename), 5000);
        }
    };

    const handleCreateCompressJobSuccess = async (
        image: CompressImage,
        job: Job,
        settings: CompressState["compressionSettings"],
    ) => {
        const compressService = new CompressService(image, job, {
            handleDisconnect: () => {},
            handleReconnect: () => {},
            handleJobComplete: (image, job) => handleCompressComplete(image, job, settings),
            handleJobFail: (image) => {
                dispatch(
                    setError({
                        imageId: image.id,
                        error: {
                            message: "compression failed",
                            state: "compressing",
                        },
                    }),
                );
            },
            onOfflineLimitReach: () => {},
        });
        await compressService.startJob();
    };

    const handleCompressComplete = (image: CompressImage, job: Job, settings: CompressState["compressionSettings"]) => {
        dispatch(
            setCompressed({
                image,
                job,
                settings,
            }),
        );
    };

    return {
        compressImage: handleCreateCompressJob,
    };
};
